import React from "react"
import {
  IconMathSymbols,
  IconLetterCaseToggle,
  IconAbc,
} from "@tabler/icons-react"
import type { GrammarEntry } from "./grammar_glossary"

const NAV_ICON_SIZE = 24
const NAV_ICON_STROKE_WIDTH = 2

/**
 * How to READ IPA (Irish edition) – the companion to the spelling-based pronunciation guide. Where
 * `pronunciationGuide` goes spelling -> sound, this goes IPA symbol -> an English-approximate sound, so a
 * learner can decode the transcription we generate with espeak-ng (e.g. /ˈsˠl̪ˠaːn̠ʲtʲə/). Scoped to the
 * symbols espeak actually emits for Irish, not the full IPA chart. English sounds are approximate and vary
 * by dialect. Verify against teanglann.ie before leaning on any single row.
 */

interface IpaRow {
  /** The IPA symbol or diacritic. */
  symbol: string
  /** An English-approximate "sounds like". */
  sound: string
  /** A short Irish example (spelling, optionally with the symbol in context). */
  example: string
}

const IpaTable: React.FC<{ rows: IpaRow[] }> = ({ rows }) => (
  <table className="w-full border-collapse text-sm" data-testid="ipa-table">
    <tbody>
      {rows.map((row) => (
        <tr
          key={row.symbol}
          className="border-t border-secondary-100 align-top"
        >
          <td className="whitespace-nowrap py-1.5 pr-3 font-mono text-sm text-foreground">
            {row.symbol}
          </td>
          <td className="py-1.5 pr-3">{row.sound}</td>
          <td className="py-1.5 italic text-muted-foreground">{row.example}</td>
        </tr>
      ))}
    </tbody>
  </table>
)

// The diacritics that trip people up most – they carry Irish's whole broad/slender system.
const MARKS: IpaRow[] = [
  {
    symbol: "ˈ",
    sound: "Stress: the syllable right after it is the loud one",
    example: "ˈmadra (dog)",
  },
  {
    symbol: "ː",
    sound: "Long vowel (a fada): hold it longer",
    example: "bád /bˠaːd̪ˠ/ (boat)",
  },
  {
    symbol: "ˠ",
    sound: "Broad consonant: 'dark', tongue pulled back",
    example: "lˠ in lá (day)",
  },
  {
    symbol: "ʲ",
    sound: "Slender consonant: adds a slight 'y' glide",
    example: "bʲ in beo (alive)",
  },
  {
    symbol: "ə",
    sound: "schwa, the 'uh' in an unstressed syllable",
    example: "the a in madra",
  },
  {
    symbol: "◌̪  ◌̠",
    sound: "Tongue position marks – read the base letter, ignore for now",
    example: "n̪ˠ, t̠ʲ",
  },
]

const VOWELS: IpaRow[] = [
  { symbol: "a", sound: "between 'cat' and 'cut'", example: "mac (son)" },
  { symbol: "ɑː / aː", sound: "'father', 'call'", example: "bád (boat)" },
  { symbol: "ɛ", sound: "'bet'", example: "te (hot)" },
  { symbol: "eː", sound: "'day' – no glide at the end", example: "é (he)" },
  { symbol: "ɪ", sound: "'bit'", example: "ith (eat)" },
  { symbol: "iː", sound: "'see'", example: "í (she)" },
  { symbol: "ɔ", sound: "'off'", example: "olc (evil)" },
  { symbol: "oː", sound: "'go' – no glide at the end", example: "ó (from)" },
  { symbol: "ʊ", sound: "'put'", example: "dubh (black)" },
  { symbol: "uː", sound: "'boot'", example: "tú (you)" },
  {
    symbol: "ə",
    sound: "'sofa' – the unstressed 'uh'",
    example: "-a in madra",
  },
]

const CONSONANTS: IpaRow[] = [
  {
    symbol: "x",
    sound: "'loch' (Scots), 'Bach' – from the throat",
    example: "ch in loch, nach",
  },
  {
    symbol: "ɣ",
    sound: "a voiced 'loch' (gargled g); no English",
    example: "broad dh/gh, dhún",
  },
  {
    symbol: "ç",
    sound: "the breathy start of 'hue', 'huge'",
    example: "slender ch, deich (ten)",
  },
  { symbol: "j", sound: "'yes'", example: "slender dh/gh, a Dhia" },
  { symbol: "w", sound: "'wow'", example: "broad bh/mh, bhí (was)" },
  { symbol: "vʲ / v", sound: "'van'", example: "slender bh/mh, mo mhéar" },
  { symbol: "ʃ", sound: "'shoe'", example: "slender s, Síle" },
  {
    symbol: "tʲ",
    sound: "'tune' (British), toward 'chew'",
    example: "slender t, te (hot)",
  },
  {
    symbol: "dʲ",
    sound: "'dune' (British), toward 'jew'",
    example: "slender d, Dia (God)",
  },
  {
    symbol: "lˠ / l̪ˠ",
    sound: "a dark 'l', as in 'pull'",
    example: "broad l, lá (day)",
  },
  {
    symbol: "ɾ / ɾˠ",
    sound: "a tapped 'r' (Spanish 'pero')",
    example: "broad r, rí (king)",
  },
  { symbol: "h", sound: "'house'", example: "sh/th, shona (happy)" },
  { symbol: "ŋ", sound: "'sing'", example: "broad ng, i nGaillimh" },
]

export const ipaGuide: GrammarEntry = {
  title: "Reading IPA (Irish)",
  // A single scannable page (no pager): it's a compact cheat-sheet, read top to bottom at a glance.
  singlePage: true,
  slides: [
    {
      heading: "What the symbols mean",
      tip: "grug not memorise chart. grug find the marks – ˈ loud, ː long, ˠ dark, ʲ has little y – then read the letters like normal.",
      body: "IPA writes one symbol per sound, so it says exactly how a word is spoken, no matter the spelling. Irish leans hard on two marks: ˠ (broad, 'dark') and ʲ (slender, a slight 'y'). Learn those plus the long mark ː and the stress mark ˈ and most transcriptions become readable. The English sounds below are approximate and vary by dialect.",
    },
    {
      heading: "Marks and diacritics",
      nav: {
        label: "Marks",
        description: "Stress, length, and the broad/slender pair.",
        example: "ˈ loud • ː long • ˠ dark • ʲ +y",
        icon: (
          <IconMathSymbols
            size={NAV_ICON_SIZE}
            stroke={NAV_ICON_STROKE_WIDTH}
          />
        ),
      },
      body: "The little marks carry the most meaning in Irish. Read these first.",
      content: <IpaTable rows={MARKS} />,
    },
    {
      heading: "Vowels",
      nav: {
        label: "Vowels",
        description: "Short vowels and their long (fada) forms.",
        example: "aː → 'call' • iː → 'see'",
        icon: (
          <IconLetterCaseToggle
            size={NAV_ICON_SIZE}
            stroke={NAV_ICON_STROKE_WIDTH}
          />
        ),
      },
      body: "A colon (ː) after a vowel makes it long – that is the fada.",
      content: <IpaTable rows={VOWELS} />,
    },
    {
      heading: "Consonants to watch",
      nav: {
        label: "Consonants",
        description: "The symbols with no obvious English letter.",
        example: "x → 'loch' • ʃ → 'shoe'",
        icon: <IconAbc size={NAV_ICON_SIZE} stroke={NAV_ICON_STROKE_WIDTH} />,
      },
      body: "Most consonants read as their English letter. These are the ones that don't.",
      content: <IpaTable rows={CONSONANTS} />,
    },
  ],
}
